import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { withDragAndDropProps } from "react-big-calendar/lib/addons/dragAndDrop";
import { fetchQuery, graphql, useFragment } from "react-relay";

import {
	addDays,
	addMinutes,
	differenceInDays,
	differenceInMinutes,
	format,
	getDay,
	getHours,
	getMinutes,
	isSameDay,
	isSameMinute,
	isWithinInterval,
	parse,
	set,
	startOfDay,
	startOfHour,
	startOfWeek,
	subDays,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { noop } from "lodash";
import store from "store2";

import {
	type MeetingGuidelinesSettings,
	MeetWithContext,
	participantEventsQuery,
	SHOW_WEEKENDS_STORE_KEY,
} from "~/calendar/CalendarPage";
import { EventFormContext } from "~/calendar/EventForm/EventFormContext";
import useFirstWeekday from "~/calendar/hooks/useFirstWeekday";
import usePreferredTimezone from "~/calendar/hooks/usePreferredTimezone";
import { simpleHash, str2AttendeeColors } from "~/lib/utils";
import { environment } from "~/models";
import { getTimezoneInfo } from "~/settings/SettingsModal/TimezoneSelector";
import { getOccurrencesListFromRepeatAndInterval } from "~/stream/utils";
import { Calendar, type CalendarProps, dateFnsLocalizer, DateLocalizer, type DayLayoutFunction } from "~/ui/calendar";
// @ts-ignore
import overlap from "~/ui/calendar/utils/layout-algorithms/overlap";
// @ts-ignore
import Week from "~/ui/calendar/Week";
// @ts-ignore
import WorkWeek from "~/ui/calendar/WorkWeek";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import type { CalendarPageParticipantEventsQuery } from "~/__generated__/CalendarPageParticipantEventsQuery.graphql";
import type { WeekPreview_viewer$key } from "~/__generated__/WeekPreview_viewer.graphql";
import type {
	WeekPreviewEventsForRangeQuery as EventsForRangeQueryOperation,
	WeekPreviewEventsForRangeQuery$data,
} from "~/__generated__/WeekPreviewEventsForRangeQuery.graphql";

import styles from "./EventFormModal.module.css";

const localizer = dateFnsLocalizer({
	format,
	parse,
	startOfWeek,
	getDay,
	locales: {
		"en-US": enUS,
	},
});

export type CalendarContext = {
	loadingEvents: boolean;
};

export const CalendarContext = createContext<CalendarContext>({
	loadingEvents: true,
});
CalendarContext.displayName = "CalendarContext";

export type Event = Omit<
	NonNullable<WeekPreviewEventsForRangeQuery$data["viewer"]["events"]["edges"][0]>["node"],
	"start" | "end"
> & {
	blockType: "real" | "imaginary" | "participant";
	start: Date;
	end: Date;
};

function normalizeRange(range: Date[] | { start: Date; end: Date }, localizer: DateLocalizer): [Date, Date] {
	let start: Date;
	let end: Date;
	if (Array.isArray(range)) {
		start = range[0] as Date;
		end = range[range.length - 1] as Date;
	} else {
		start = range.start;
		end = range.end;
	}
	// @ts-ignore
	return [localizer.startOf(start, "day"), localizer.endOf(end, "day")];
}

const weekPreviewViewer = graphql`
	fragment WeekPreview_viewer on Viewer {
		...usePreferredTimezone_viewer

		settings {
			timezonePreferences {
				preferredTimezone {
					userLabel
					timezone {
						name
					}
				}
				extraTimezones {
					userLabel
					timezone {
						name
					}
				}
			}
		}
	}
`;

// TODO: only use needed fields
const eventsQuery = graphql`
	query WeekPreviewEventsForRangeQuery($end: DateTime, $start: DateTime) {
		viewer {
			events: eventsByUserEventFilters(start: $start, end: $end) {
				edges {
					node {
						id
						guid
						start
						end
						title
						isAllDay
						isOrganizer
						status
						userResponseStatus
						providerEventColor: color
						calendarColor
						fellowColor
						isHidden
						type
						conferenceCalls {
							provider
							url
						}
						calendar {
							id
							source
						}
						timeBlockType
					}
				}
			}
		}
	}
`;

function CalendarLoadingSpinner() {
	const { loadingEvents } = useContext(CalendarContext);

	if (!loadingEvents) return null;
	return (
		<span style={{ marginRight: 12 }}>
			<LoadingSpinner inline size="xs" dimmer={false} delay={200} />
		</span>
	);
}

interface WeekPreviewProps {
	viewer: WeekPreview_viewer$key;
	eventToEditGuid?: string;
	meetingGuidelinesSettings: MeetingGuidelinesSettings;
}

export default function WeekPreview({
	viewer: viewerKey,
	eventToEditGuid,
	meetingGuidelinesSettings,
}: WeekPreviewProps) {
	const { eventTitle, repeatState, customRrule, timeState, changeTime } = useContext(EventFormContext);
	const { meetWithParticipants } = useContext(MeetWithContext);

	const viewer = useFragment(weekPreviewViewer, viewerKey);

	const { systemToUserTime } = usePreferredTimezone(viewer);
	const firstWeekday = useFirstWeekday();
	const showWeekends = useMemo(() => !!store.get(SHOW_WEEKENDS_STORE_KEY), []);

	const [date, setDate] = useState<Date>(timeState.start);
	useEffect(() => setDate(timeState.start), [timeState.start]);

	const [events, setEvents] = useState<Event[]>([]);
	const [participantEvents, setParticipantEvents] = useState<{ [email: string]: Event[] }>({});

	const [loadingEvents, setLoadingEvents] = useState(true);
	const [loadingParticipantEvents, setLoadingParticipantEvents] = useState(false);
	const [hasEventsInRange, setHasEventsInRange] = useState(false);

	const loadEventsForRange = useCallback(
		async (unzonedStart: Date, unzonedEnd: Date) => {
			setLoadingEvents(true);

			const start = systemToUserTime(unzonedStart);
			const end = systemToUserTime(unzonedEnd);
			const extendedStart = subDays(start, 7);
			const extendedEnd = addDays(end, 7);

			const response = await fetchQuery<EventsForRangeQueryOperation>(
				environment,
				eventsQuery,
				{
					start: extendedStart.toISOString(),
					end: extendedEnd.toISOString(),
				},
				{ fetchPolicy: "network-only" },
			).toPromise();

			const responseEvents =
				response?.viewer.events.edges
					.map(edge => edge && edge.node)
					.filter((e): e is NonNullable<typeof e> => e !== null) ?? [];

			const events = responseEvents.reduce((runningEv, ev) => {
				if (ev.start === null || ev.end === null || ev.guid === eventToEditGuid) return runningEv;
				const evStart = systemToUserTime(new Date(ev.start));
				const evEnd = systemToUserTime(new Date(ev.end));

				return [
					...runningEv,
					{
						...ev,
						blockType: "real" as const,
						title: ev.title === "" ? "(No title)" : ev.title,
						start: ev.isAllDay ? startOfDay(evStart) : evStart,
						end: ev.isAllDay ? startOfDay(evEnd) : evEnd,
					},
				];
			}, new Array<Event>());

			const participantQueries = meetWithParticipants.slice(0, 10).map(({ email, id }) =>
				fetchQuery<CalendarPageParticipantEventsQuery>(
					environment,
					participantEventsQuery,
					{
						participantEmail: email || "",
						participantId: id,
						start: extendedStart.toISOString(),
						end: extendedEnd.toISOString(),
					},
					{ fetchPolicy: "store-or-network" },
				).toPromise(),
			);

			setLoadingParticipantEvents(true);
			const participantResponses = await Promise.allSettled(participantQueries);
			const participantEvents: { [k: string]: Event[] } = participantResponses.reduce((prev, response, index) => {
				if (response.status === "fulfilled") {
					const responseEvents =
						response.value?.viewer.eventsByUser?.edges
							.map(edge => edge && edge.node)
							.filter((e): e is NonNullable<typeof e> => e != null)
							.map((e, i) => {
								const userName = meetWithParticipants[index].fullName || meetWithParticipants[index].email || "";
								const color = str2AttendeeColors(userName).ring;

								return {
									blockType: "participant",
									title: e.title || "busy",
									isAllDay: Boolean(e.isAllDay),
									start: systemToUserTime(new Date(e.start)),
									end: systemToUserTime(new Date(e.end)),
									guid: `${simpleHash(`${userName} - ${e.title} - ${e.start}`).toString()}-${i}`,
									calendarColor: `#${color}`,
									userResponseStatus: e.userResponseStatus,
									fellowColor: "CALENDAR",
								};
							}) ?? [];

					return {
						...prev,
						[meetWithParticipants[index].email || ""]: responseEvents,
					};
				} else {
					// If the promise is not fulfilled it was likely an invalid email or an email outside of the workspace
					// Ignore it.
					return prev;
				}
			}, {});

			setEvents(events);
			setParticipantEvents(participantEvents);
			setLoadingEvents(false);
			setLoadingParticipantEvents(false);
		},
		[meetWithParticipants, eventToEditGuid, systemToUserTime],
	);

	const previewEvents: Event[] = useMemo(() => {
		const range = normalizeRange(Week.range(date, { localizer }), localizer);

		const start = timeState.start;
		const end = timeState.end;
		const isAllDay = timeState.isAllDay;

		const basePreviewEvent: Omit<Event, "start" | "end"> = {
			isAllDay: isAllDay,
			title: eventTitle || "(No title)",
			blockType: "imaginary",
			guid: "imaginary-0",
			status: "confirmed",
			userResponseStatus: "none",
			calendarColor: null,
			providerEventColor: null,
			fellowColor: "CALENDAR",
			isOrganizer: true,
			id: "",
			isHidden: false,
			type: "SINGLE",
			timeBlockType: null,
			conferenceCalls: [],
			calendar: {
				id: "internal",
				source: "internal",
			},
		};

		const eventDuration = differenceInMinutes(end, start);
		const occurrences: string[] =
			repeatState != "NEVER"
				? getOccurrencesListFromRepeatAndInterval(repeatState, start, range[0], range[1], customRrule ?? undefined)
				: [start.toISOString()];

		return occurrences.map(occurrence => ({
			...basePreviewEvent,
			guid: `imaginary-${occurrence}`,
			start: new Date(occurrence),
			end: addMinutes(new Date(occurrence), eventDuration),
		}));
	}, [date, timeState, eventTitle, repeatState, customRrule]);

	const getRange = useMemo(() => {
		const WeekComponent = showWeekends ? Week : WorkWeek;
		return WeekComponent.range;
	}, [showWeekends]);

	const onRangeChange = useCallback(
		range => {
			const [start, end] = normalizeRange(range, localizer);
			loadEventsForRange(start, end);
		},
		[loadEventsForRange],
	);

	useEffect(() => {
		const range = getRange(date, { localizer });
		onRangeChange(range);
	}, [date, getRange, onRangeChange]);

	const onDateChange = useCallback(
		(newDate: Date) => {
			setDate(newDate);
			changeTime({ type: "SHIFT", newTime: addMinutes(startOfHour(newDate), 60) });
		},
		[changeTime],
	);

	const interactivityCondition = useCallback(({ blockType }: Event) => blockType === "imaginary", []);

	const onSelectSlot = useCallback<NonNullable<CalendarProps<Event>["onSelectSlot"]>>(
		({ start, end, action, isAllDay }) => {
			if (action === "click") return;

			if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

			changeTime({ type: "CHANGE_START", newTime: start });
			changeTime({ type: "CHANGE_END", newTime: end });
			if (isAllDay !== timeState.isAllDay) {
				changeTime({ type: "TOGGLE_ALL_DAY" });
			}
		},
		[changeTime, timeState.isAllDay],
	);

	const onEventDrop = useCallback<NonNullable<withDragAndDropProps<Event>["onEventDrop"]>>(
		async ({ event, ...dropSlot }) => {
			switch (event.blockType) {
				case "imaginary": {
					changeTime({ type: "SHIFT", newTime: new Date(dropSlot.start) });
					if (event.isAllDay && !dropSlot.isAllDay) {
						// dragged from all-day section
						changeTime({ type: "CHANGE_END", newTime: addMinutes(new Date(dropSlot.start), 60) });
					}
					if (Boolean(dropSlot.isAllDay) != event.isAllDay) changeTime({ type: "TOGGLE_ALL_DAY" });
					return;
				}
				default:
					return;
			}
		},
		[changeTime],
	);

	const onEventResize = useCallback<NonNullable<withDragAndDropProps<Event>["onEventResize"]>>(
		async ({ event, ...newSlot }) => {
			switch (event.blockType) {
				case "imaginary": {
					const newSlotStart = new Date(newSlot.start);
					const newSlotEnd = new Date(newSlot.end);
					const eventStart = new Date(event.start);
					const eventEnd = new Date(event.end);

					if (newSlot.isAllDay) {
						if (!isSameDay(eventStart, newSlotStart)) {
							changeTime({
								type: "CHANGE_START",
								newTime: set(newSlotStart, {
									hours: getHours(eventStart),
									minutes: getMinutes(eventStart),
								}),
							});
						}

						if (!isSameDay(eventEnd, newSlotEnd)) {
							changeTime({
								type: "CHANGE_END",
								newTime: set(newSlotEnd, {
									hours: getHours(eventEnd),
									minutes: getMinutes(eventEnd),
								}),
							});
						}
					} else {
						if (!isSameMinute(eventStart, newSlotStart)) {
							changeTime({
								type: "CHANGE_START",
								newTime: set(timeState.start, {
									hours: getHours(newSlotStart),
									minutes: getMinutes(newSlotStart),
								}),
							});
						}

						if (!isSameMinute(eventEnd, newSlotEnd)) {
							changeTime({
								type: "CHANGE_END",
								newTime: set(timeState.end, {
									hours: getHours(newSlotEnd),
									minutes: getMinutes(newSlotEnd),
								}),
							});
						}
					}
					return;
				}
				default:
					return;
			}
		},
		[changeTime, timeState.start, timeState.end],
	);

	const dayLayoutAlgorithm = useCallback<DayLayoutFunction<Event>>(({ events, slotMetrics, accessors }) => {
		type EventStyle = React.CSSProperties & { xOffset: number };
		const [layerOneEvents, layerTwoEvents] = events.reduce(
			([prevLayerOne, prevLayerTwo], event) => {
				if (event.isAllDay || ["real", "participant"].includes(event.blockType)) {
					return [[...prevLayerOne, event], prevLayerTwo];
				} else {
					return [prevLayerOne, [...prevLayerTwo, event]];
				}
			},
			[new Array<Event>(), new Array<Event>()],
		);

		const layerOneEventsWithStyles: Array<{ event: Event; style: EventStyle }> = overlap({
			events: layerOneEvents,
			minimumStartDifference: 15,
			slotMetrics,
			accessors,
		});

		const layerTwoEventsWithStyles: Array<{ event: Event; style: EventStyle }> = overlap({
			events: layerTwoEvents,
			minimumStartDifference: 15,
			slotMetrics,
			accessors,
		});

		const addVerticalPadding = ({ event, style }: { event: Event; style: EventStyle }) => {
			if (typeof style.height === "number" && typeof style.top === "number") {
				return {
					event,
					style: {
						...style,
						height: `calc(${style.height}% - 2px)`,
					},
				};
			}

			return { event, style };
		};

		return [...layerOneEventsWithStyles, ...layerTwoEventsWithStyles].map(addVerticalPadding);
	}, []);

	const calendarTimezones = React.useMemo(() => {
		const { preferredTimezone, extraTimezones } = viewer.settings.timezonePreferences;
		type CalendarTimezone = { label: string; name: string };
		let timezones: { [key in "preferredTimezone"]: CalendarTimezone } & {
			[key in "secondTimezone" | "thirdTimezone"]?: CalendarTimezone;
		} = {
			preferredTimezone: {
				label: preferredTimezone.userLabel,
				name: getTimezoneInfo(preferredTimezone.timezone.name).name,
			},
		};

		if (extraTimezones && extraTimezones[0]) {
			timezones["secondTimezone"] = {
				label: extraTimezones[0].userLabel,
				name: getTimezoneInfo(extraTimezones[0].timezone.name).name,
			};

			if (extraTimezones[1]) {
				timezones["thirdTimezone"] = {
					label: extraTimezones[1].userLabel,
					name: getTimezoneInfo(extraTimezones[1].timezone.name).name,
				};
			}
		}

		return timezones;
	}, [viewer.settings.timezonePreferences]);

	const isAllDayLike = useCallback((event: Event) => {
		if (event.isAllDay) return true;

		// Some events can span multiple days but not be "all day" events
		const eventInDays = differenceInDays(event.end, event.start);
		return eventInDays > 0;
	}, []);

	const allEvents = useMemo(
		() => [...events, ...Object.values(participantEvents).flat(), ...previewEvents],
		[events, participantEvents, previewEvents],
	);

	useEffect(() => {
		const WeekComponent = showWeekends ? Week : WorkWeek;

		const range = WeekComponent.range(date, { localizer }) as Date[];
		const rangeStart = range[0];
		const rangeEnd = range[range.length - 1];
		const eventsInRange = events?.filter(
			e => e.start && isWithinInterval(new Date(e.start), { start: rangeStart, end: rangeEnd }),
		);
		setHasEventsInRange(eventsInRange && eventsInRange.length > 0);
	}, [events, date, showWeekends]);

	return (
		<div className={styles.calendar}>
			<CalendarContext.Provider
				value={useMemo(
					() => ({ loadingEvents: (loadingEvents && !hasEventsInRange) || loadingParticipantEvents }),
					[loadingEvents, loadingParticipantEvents, hasEventsInRange],
				)}
			>
				<Calendar<Event>
					firstWeekday={firstWeekday}
					step={15}
					timeslots={4}
					date={date}
					getNow={useCallback(() => systemToUserTime(new Date()), [systemToUserTime])}
					onNavigate={onDateChange}
					events={allEvents}
					showMultiDayTimes
					// TODO: Pull this into the library
					allDayAccessor={isAllDayLike}
					resizableCondition={interactivityCondition}
					draggableCondition={interactivityCondition}
					selectableCondition={interactivityCondition}
					selectable="ignoreEvents"
					onEventDrop={onEventDrop}
					onEventResize={onEventResize}
					onSelectSlot={onSelectSlot}
					// TODO: Pull this into the library
					dayLayoutAlgorithm={dayLayoutAlgorithm}
					onView={noop}
					view="week"
					// TODO: Make this so you don't have to import these components
					views={{
						week: showWeekends ? Week : WorkWeek,
					}}
					components={{
						toolbarExtras: { right: CalendarLoadingSpinner },
					}}
					meetingGuidelinesSettings={meetingGuidelinesSettings}
					timezones={calendarTimezones}
				/>
			</CalendarContext.Provider>
		</div>
	);
}
